import { useEffect, useState } from 'react';
import { fetchReplayLaunchControlsStatus, type LaunchAssetClass } from './replay/launchControls';

export type AssetClassKey = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';
export type MarketTypeWithAll = 'ALL' | AssetClassKey;
export type AssetClassVisibility = Record<AssetClassKey, boolean>;
export type AssetClassVisibilityState = {
  isLoading: boolean;
  isResolved: boolean;
};

export const ASSET_CLASS_VISIBILITY_STORAGE_KEY = 'tt_asset_class_visibility_v1';
export const ASSET_CLASS_VISIBILITY_EVENT = 'tt:asset-class-visibility-changed';

export const ASSET_CLASS_ORDER: AssetClassKey[] = ['STOCKS', 'FUTURES', 'FOREX', 'CRYPTO'];

const DEFAULT_ASSET_CLASS_VISIBILITY: AssetClassVisibility = {
  STOCKS: true,
  FUTURES: false,
  FOREX: false,
  CRYPTO: false,
};

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value !== 0 : fallback;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true;
    if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false;
  }
  return fallback;
}

function sanitizeVisibility(raw: unknown): AssetClassVisibility {
  const obj = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? (raw as Record<string, unknown>) : {};
  return {
    STOCKS: normalizeBoolean(obj.STOCKS, DEFAULT_ASSET_CLASS_VISIBILITY.STOCKS),
    FUTURES: normalizeBoolean(obj.FUTURES, DEFAULT_ASSET_CLASS_VISIBILITY.FUTURES),
    FOREX: normalizeBoolean(obj.FOREX, DEFAULT_ASSET_CLASS_VISIBILITY.FOREX),
    CRYPTO: normalizeBoolean(obj.CRYPTO, DEFAULT_ASSET_CLASS_VISIBILITY.CRYPTO),
  };
}

function visibilityFromEnabledAssetClasses(enabledAssetClasses: LaunchAssetClass[]): AssetClassVisibility {
  const enabled = new Set(enabledAssetClasses);
  return {
    STOCKS: enabled.has('STOCKS'),
    FUTURES: enabled.has('FUTURES'),
    FOREX: enabled.has('FOREX'),
    CRYPTO: enabled.has('CRYPTO'),
  };
}

export function getDefaultAssetClassVisibility(): AssetClassVisibility {
  return { ...DEFAULT_ASSET_CLASS_VISIBILITY };
}

export function readAssetClassVisibility(): AssetClassVisibility {
  return getDefaultAssetClassVisibility();
}

export function writeAssetClassVisibility(next: AssetClassVisibility): void {
  const safe = sanitizeVisibility(next);

  try {
    window.dispatchEvent(new CustomEvent(ASSET_CLASS_VISIBILITY_EVENT, { detail: safe }));
  } catch {
    // ignore custom event failures
  }
}

export function getEnabledAssetClasses(visibility: AssetClassVisibility): AssetClassKey[] {
  return ASSET_CLASS_ORDER.filter((assetClass) => visibility[assetClass]);
}

export function isAssetClassEnabled(assetClass: AssetClassKey, visibility: AssetClassVisibility): boolean {
  return Boolean(visibility[assetClass]);
}

export function coerceMarketTypeToVisible(
  marketType: MarketTypeWithAll,
  visibility: AssetClassVisibility,
  options?: { fallbackToAll?: boolean },
): MarketTypeWithAll {
  const enabled = getEnabledAssetClasses(visibility);

  if (marketType === 'ALL') {
    if (enabled.length >= 2) return 'ALL';
    if (enabled.length === 1) return enabled[0];
    return options?.fallbackToAll ? 'ALL' : 'STOCKS';
  }

  if (isAssetClassEnabled(marketType, visibility)) return marketType;

  if (enabled.length >= 2 && options?.fallbackToAll) return 'ALL';
  if (enabled.length > 0) return enabled[0];
  return options?.fallbackToAll ? 'ALL' : 'STOCKS';
}

export function getVisibleMarketTypes(visibility: AssetClassVisibility): MarketTypeWithAll[] {
  const enabled = getEnabledAssetClasses(visibility);
  if (enabled.length >= 2) return ['ALL', ...enabled];
  if (enabled.length === 1) return [enabled[0]];
  return ['STOCKS'];
}

export function shouldShowAssetClassSelector(visibility: AssetClassVisibility): boolean {
  return getEnabledAssetClasses(visibility).length > 1;
}

export function isTradeAssetClassVisible(assetClass: string | null | undefined, visibility: AssetClassVisibility): boolean {
  const normalized = String(assetClass || '').trim().toUpperCase();
  if (!normalized) return true;
  if (normalized === 'STOCKS') return visibility.STOCKS;
  if (normalized === 'FUTURES') return visibility.FUTURES;
  if (normalized === 'FOREX') return visibility.FOREX;
  if (normalized === 'CRYPTO') return visibility.CRYPTO;
  return true;
}

export function useAssetClassVisibility() {
  const [visibility, setVisibility] = useState<AssetClassVisibility>(() => readAssetClassVisibility());
  const [state, setState] = useState<AssetClassVisibilityState>({ isLoading: true, isResolved: false });

  useEffect(() => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let cancelled = false;

    const refresh = async () => {
      try {
        const status = await fetchReplayLaunchControlsStatus({ signal: controller?.signal });
        if (cancelled) return;
        if (status?.enabledAssetClasses?.length) {
          setVisibility(visibilityFromEnabledAssetClasses(status.enabledAssetClasses));
        } else {
          setVisibility(getDefaultAssetClassVisibility());
        }
        setState({ isLoading: false, isResolved: true });
      } catch {
        if (!cancelled) {
          setVisibility(getDefaultAssetClassVisibility());
          setState({ isLoading: false, isResolved: true });
        }
      }
    };

    const onChanged = () => {
      void refresh();
    };

    void refresh();
    window.addEventListener(ASSET_CLASS_VISIBILITY_EVENT, onChanged as EventListener);
    return () => {
      cancelled = true;
      controller?.abort();
      window.removeEventListener(ASSET_CLASS_VISIBILITY_EVENT, onChanged as EventListener);
    };
  }, []);

  const updateVisibility = (next: AssetClassVisibility | ((prev: AssetClassVisibility) => AssetClassVisibility)) => {
    const resolved = typeof next === 'function'
      ? (next as (prev: AssetClassVisibility) => AssetClassVisibility)(visibility)
      : next;
    writeAssetClassVisibility(resolved);
    setVisibility(sanitizeVisibility(resolved));
    setState({ isLoading: false, isResolved: true });
  };

  return [visibility, updateVisibility, state] as const;
}
